import { Cpu, ShieldCheck, Sparkles, Settings as SettingsIcon, Users, FileText, ScrollText, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";

// ---------- Credit Engine ----------
export function CreditEngine() {
    const RULES = [
        { name: "KYC verification", weight: "+80", desc: "User completed identity verification" },
        { name: "Wallet activity (≥50 txns)", weight: "+60", desc: "Strong transactional behavior" },
        { name: "Repayment track record", weight: "+25 / completed loan, capped 120", desc: "Historical repayment performance" },
        { name: "Overdue penalty", weight: "−60 / loan, capped 200", desc: "Active overdue obligations" },
        { name: "Verified income flow", weight: "+50", desc: "Salary or business inflows confirmed" },
        { name: "Long platform tenure (90d+)", weight: "+40", desc: "Time on platform" },
        { name: "Concurrent loans (3+)", weight: "−30", desc: "Excessive concurrent utilization" },
        { name: "Strong business revenue (₦1M+)", weight: "+60", desc: "MSME revenue band" },
    ];
    return (
        <div data-testid="credit-engine-page">
            <h1 className="font-display text-2xl font-semibold text-[#0A1930]">Credit Engine</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Credit DNA Score™ rule weights & AI explanation layer</p>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Cpu size={20} /></div>
                    <div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Engine version</div>
                        <div className="font-display text-lg font-bold">v2.4 · Hybrid</div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><ShieldCheck size={20} /></div>
                    <div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Status</div>
                        <div className="font-display text-lg font-bold text-emerald-600">Healthy</div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center"><Sparkles size={20} /></div>
                    <div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">AI explainer</div>
                        <div className="font-display text-lg font-bold">Claude Sonnet 4.5</div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 product-shadow overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100">
                    <h3 className="font-display font-semibold text-[#0A1930]">Active scoring rules</h3>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left text-xs uppercase tracking-wider font-bold text-slate-500">
                            <th className="px-5 py-3">Rule</th>
                            <th className="px-5 py-3">Weight</th>
                            <th className="px-5 py-3">Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        {RULES.map((r, i) => (
                            <tr key={i} className="border-b border-slate-100" data-testid={`rule-row-${i}`}>
                                <td className="px-5 py-3 font-semibold">{r.name}</td>
                                <td className="px-5 py-3 font-mono text-xs">{r.weight}</td>
                                <td className="px-5 py-3 text-slate-600">{r.desc}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Transactions ----------
export function AdminTransactions() {
    const [txns, setTxns] = useState([]);
    useEffect(() => { api.get("/admin/transactions").then((r) => setTxns(r.data)); }, []);
    return (
        <div data-testid="admin-transactions-page">
            <h1 className="font-display text-2xl font-semibold text-[#0A1930]">Transactions</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">{txns.length} platform-wide transactions</p>
            <div className="bg-white rounded-2xl border border-slate-200 product-shadow overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left text-xs uppercase tracking-wider font-bold text-slate-500">
                            <th className="px-5 py-3">User</th>
                            <th className="px-5 py-3">Description</th>
                            <th className="px-5 py-3">Category</th>
                            <th className="px-5 py-3">Amount</th>
                            <th className="px-5 py-3">Type</th>
                        </tr>
                    </thead>
                    <tbody>
                        {txns.slice(0, 200).map((t) => (
                            <tr key={t.id} className="border-b border-slate-100" data-testid={`txn-row-${t.id}`}>
                                <td className="px-5 py-3 font-medium">{t.user_name}</td>
                                <td className="px-5 py-3 text-slate-700">{t.description}</td>
                                <td className="px-5 py-3"><Badge variant="outline" className="capitalize">{t.category}</Badge></td>
                                <td className={`px-5 py-3 font-mono font-bold ${t.type === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                                    {t.type === "credit" ? "+" : "−"}₦{t.amount.toLocaleString()}
                                </td>
                                <td className="px-5 py-3 text-xs uppercase font-bold text-slate-500">{t.type}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Collections ----------
export function AdminCollections() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/admin/collections").then((r) => setItems(r.data)); }, []);
    return (
        <div data-testid="admin-collections-page">
            <h1 className="font-display text-2xl font-semibold text-[#0A1930]">Collections</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">{items.length} loans in collections workflow · click any row to log contact</p>
            <div className="bg-white rounded-2xl border border-slate-200 product-shadow overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left text-xs uppercase tracking-wider font-bold text-slate-500">
                            <th className="px-5 py-3">Borrower</th>
                            <th className="px-5 py-3">Phone</th>
                            <th className="px-5 py-3">Product</th>
                            <th className="px-5 py-3">Outstanding</th>
                            <th className="px-5 py-3">Due</th>
                            <th className="px-5 py-3">Status</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((l) => (
                            <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`collection-${l.id}`}>
                                <td className="px-5 py-3 font-medium">{l.borrower}</td>
                                <td className="px-5 py-3 font-mono text-xs text-slate-500">{l.phone}</td>
                                <td className="px-5 py-3"><Badge variant="outline">{l.product}</Badge></td>
                                <td className="px-5 py-3 font-mono font-bold">₦{(l.total_repayment - (l.repaid_amount || 0)).toLocaleString()}</td>
                                <td className="px-5 py-3 text-xs text-slate-500">{l.due_date ? new Date(l.due_date).toLocaleDateString() : "—"}</td>
                                <td className="px-5 py-3">
                                    <Badge className={l.status === "overdue" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-blue-50 text-blue-700 border-blue-200"}>
                                        {l.status}
                                    </Badge>
                                </td>
                                <td className="px-5 py-3">
                                    <a href={`/admin/loans/${l.id}`} className="text-[#0066FF] text-xs font-bold hover:underline" data-testid={`collection-open-${l.id}`}>
                                        Open & log →
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Reports ----------
export function AdminReports() {
    const REPORTS = [
        { title: "Portfolio Performance Report", desc: "Disbursement, repayment, NPL by product", color: "#0066FF" },
        { title: "Collections Performance", desc: "Aging buckets, contact rate, recovery", color: "#00D084" },
        { title: "MSME Cashflow Insights", desc: "Sales, expenses, net cashflow trends", color: "#FF6B00" },
        { title: "Credit DNA Score Distribution", desc: "Score bands across portfolio", color: "#7000FF" },
        { title: "Fraud & Risk Signals", desc: "Velocity, device mismatch, duplicate ID", color: "#FF0055" },
        { title: "Revenue & Treasury", desc: "Interest income, fees, settlement P&L", color: "#06B6D4" },
    ];
    return (
        <div data-testid="admin-reports-page">
            <h1 className="font-display text-2xl font-semibold text-[#0A1930]">Reports</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Download portfolio analytics</p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {REPORTS.map((r, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow tap-feedback cursor-pointer" data-testid={`report-${i}`}>
                        <div className="size-11 rounded-xl flex items-center justify-center" style={{ background: `${r.color}15`, color: r.color }}>
                            <FileText size={20} />
                        </div>
                        <div className="font-display font-semibold text-[#0A1930] mt-4">{r.title}</div>
                        <div className="text-xs text-slate-500 mt-1">{r.desc}</div>
                        <div className="mt-3 text-xs font-bold" style={{ color: r.color }}>Generate report →</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------- Risk & Fraud ----------
export function AdminRisk() {
    const [data, setData] = useState({ flagged_loans: [], fraud_signals: [] });
    useEffect(() => { api.get("/admin/risk").then((r) => setData(r.data)); }, []);
    return (
        <div data-testid="admin-risk-page">
            <h1 className="font-display text-2xl font-semibold text-[#0A1930]">Risk & Fraud</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Active fraud signals and flagged accounts</p>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl border border-slate-200 product-shadow p-5">
                    <h3 className="font-display font-semibold text-[#0A1930] mb-4 flex items-center gap-2">
                        <ShieldAlert size={18} className="text-rose-600" /> Active fraud signals
                    </h3>
                    <div className="space-y-2">
                        {data.fraud_signals.map((f) => (
                            <div key={f.id} className="border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid={`fraud-${f.id}`}>
                                <div className={`size-9 rounded-lg flex items-center justify-center ${f.severity === "high" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-600"}`}>
                                    <ShieldAlert size={16} />
                                </div>
                                <div className="flex-1">
                                    <div className="font-semibold text-sm">{f.type}</div>
                                    <div className="text-xs text-slate-500">User: {f.user}</div>
                                </div>
                                <Badge className={`${f.severity === "high" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"} capitalize`}>
                                    {f.severity}
                                </Badge>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 product-shadow p-5">
                    <h3 className="font-display font-semibold text-[#0A1930] mb-4">Flagged loans</h3>
                    <div className="space-y-2">
                        {data.flagged_loans.length === 0 && <div className="text-sm text-slate-500">No flagged loans</div>}
                        {data.flagged_loans.map((l) => (
                            <div key={l.id} className="border border-slate-200 rounded-xl p-3 flex items-center justify-between" data-testid={`flagged-${l.id}`}>
                                <div>
                                    <div className="font-semibold text-sm">{l.product} · {l.id.slice(0, 8)}</div>
                                    <div className="text-xs text-slate-500">Amount ₦{l.amount.toLocaleString()}</div>
                                </div>
                                <Badge className="bg-rose-50 text-rose-700 border-rose-200 capitalize">{l.status}</Badge>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------- Settings ----------
export function AdminSettings() {
    const [flags, setFlags] = useState([]);
    const [busy, setBusy] = useState({});
    const [sentStatus, setSentStatus] = useState(null);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const refresh = () => api.get("/admin/flags").then((r) => setFlags(r.data.flags || []));
    const loadSentStatus = () => api.get("/admin/flags/integrations/sentinel/status").then((r) => setSentStatus(r.data)).catch(() => {});
    useEffect(() => { refresh(); loadSentStatus(); }, []);

    const toggle = async (key, enabled) => {
        setBusy((b) => ({ ...b, [key]: true }));
        try {
            await api.post(`/admin/flags/${key}`, { enabled });
            refresh();
            if (key === "sentinel_real") loadSentStatus();
        } finally {
            setBusy((b) => ({ ...b, [key]: false }));
        }
    };

    const testSentinel = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const r = await api.post("/admin/flags/integrations/sentinel/test");
            setTestResult(r.data);
        } catch (err) {
            setTestResult({ ok: false, error: "request_failed", message: err?.response?.data?.detail || err.message });
        } finally { setTesting(false); }
    };

    return (
        <div data-testid="admin-settings-page">
            <h1 className="font-display text-2xl font-semibold text-[#0A1930]">Settings</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Platform configuration</p>

            <h2 className="font-display text-base font-semibold text-[#0A1930] mb-3 flex items-center gap-2">
                <Cpu size={16} className="text-[#0066FF]" /> Integration feature flags
            </h2>
            <p className="text-xs text-slate-500 mb-4">
                Toggle these to flip integrations from mock to real. Real integrations require the listed environment variables. If keys are missing, the platform silently falls back to mocks.
            </p>
            <div className="space-y-3 max-w-3xl">
                {flags.map((f) => (
                    <div
                        key={f.key}
                        className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow flex items-start gap-4"
                        data-testid={`flag-row-${f.key}`}
                    >
                        <div className={`size-10 rounded-xl flex items-center justify-center flex-shrink-0 ${f.enabled ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                            <ShieldCheck size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-display font-semibold text-[#0A1930]">{f.label}</span>
                                <Badge className={f.enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}>
                                    {f.enabled ? "ENABLED" : "DISABLED"}
                                </Badge>
                                <span className="text-[10px] font-mono text-slate-400">{f.key}</span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{f.description}</p>
                            {f.requires_env && f.requires_env.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Requires:</span>
                                    {f.requires_env.map((e) => (
                                        <code key={e} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded font-mono text-slate-700">{e}</code>
                                    ))}
                                </div>
                            )}
                            {f.updated_at && (
                                <div className="text-[10px] text-slate-400 mt-2">
                                    Last toggled by {f.updated_by || "—"} · {new Date(f.updated_at).toLocaleString()}
                                </div>
                            )}
                        </div>
                        <Switch
                            checked={f.enabled}
                            disabled={busy[f.key]}
                            onCheckedChange={(v) => toggle(f.key, v)}
                            data-testid={`flag-toggle-${f.key}`}
                        />
                    </div>
                ))}
            </div>

            {/* Sentinel-2 / EOSDA diagnostic panel */}
            <div className="mt-4 max-w-3xl bg-white rounded-2xl border border-slate-200 p-5 product-shadow" data-testid="sentinel-diagnostic">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                        <h3 className="font-display font-semibold text-[#0A1930] flex items-center gap-2">
                            <Sparkles size={16} className="text-emerald-600" /> Sentinel-2 / EOSDA — Live Diagnostic
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">Verify your API key + flag wiring with a real EOSDA probe.</p>
                    </div>
                    <button onClick={testSentinel} disabled={testing} className="text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg disabled:opacity-50" data-testid="sentinel-test-btn">
                        {testing ? "Testing…" : "Test connection"}
                    </button>
                </div>
                {sentStatus && (
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        <div className="bg-slate-50 rounded-lg p-2.5">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Flag</div>
                            <div className={`mt-0.5 font-bold ${sentStatus.flag_enabled ? "text-emerald-600" : "text-slate-500"}`}>{sentStatus.flag_enabled ? "ON" : "OFF"}</div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2.5">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">API key</div>
                            <div className={`mt-0.5 font-bold ${sentStatus.api_key_present ? "text-emerald-600" : "text-rose-600"}`}>
                                {sentStatus.api_key_present ? sentStatus.key_preview : "Missing"}
                            </div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2.5">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Mode</div>
                            <div className={`mt-0.5 font-bold ${sentStatus.status === "live" ? "text-emerald-600" : "text-slate-500"}`}>{sentStatus.mode}</div>
                        </div>
                    </div>
                )}
                {testResult && (
                    <div className={`mt-3 rounded-lg p-3 text-xs ${testResult.ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`} data-testid="sentinel-test-result">
                        <div className="font-bold">{testResult.ok ? "✅ EOSDA Live" : "⚠ Test failed"}</div>
                        <p className="mt-1">{testResult.message}</p>
                        {testResult.sample && (
                            <div className="mt-2 font-mono text-[11px]">NDVI: {testResult.sample.ndvi ?? "—"} · Soil: {testResult.sample.soil_moisture ?? "—"} · Rain: {testResult.sample.rain_mm ?? "—"}mm · Temp: {testResult.sample.temp_c ?? "—"}°C</div>
                        )}
                    </div>
                )}
                {!sentStatus?.api_key_present && (
                    <div className="mt-3 bg-amber-50 rounded-lg p-3 text-xs text-amber-900">
                        <strong>To activate:</strong> Sign up at <a href="https://api-connect.eos.com" target="_blank" rel="noreferrer" className="underline font-semibold">api-connect.eos.com</a>, copy your API key into <code className="bg-amber-100 px-1 py-0.5 rounded font-mono">EOSDA_API_KEY</code> in <code className="bg-amber-100 px-1 py-0.5 rounded font-mono">/app/backend/.env</code>, then toggle the Sentinel-2 flag ON above.
                    </div>
                )}
            </div>

            <h2 className="font-display text-base font-semibold text-[#0A1930] mt-10 mb-3">Other configuration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                <Setting icon={Users} title="Team & Access" desc="Manage admin users, roles and permissions" />
                <Setting icon={SettingsIcon} title="Platform" desc="Currencies, regions, business hours" />
                <Setting icon={ShieldCheck} title="Compliance" desc="KYC providers, AML rules, audit logs" />
                <Setting icon={Cpu} title="Risk Thresholds" desc="Score bands, NPL bucket definitions" />
            </div>
        </div>
    );
}

function Setting({ icon: Icon, title, desc }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow tap-feedback cursor-pointer" data-testid={`setting-${title.replace(/\s/g, "")}`}>
            <div className="size-11 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center"><Icon size={20} /></div>
            <div className="font-display font-semibold text-[#0A1930] mt-4">{title}</div>
            <div className="text-xs text-slate-500 mt-1">{desc}</div>
        </div>
    );
}

// ---------- System Logs ----------
export function AdminLogs() {
    const [logs, setLogs] = useState([]);
    useEffect(() => { api.get("/admin/system-logs").then((r) => setLogs(r.data)); }, []);
    const COLOR = { INFO: "text-blue-600", WARN: "text-amber-600", ERROR: "text-rose-600" };
    return (
        <div data-testid="admin-logs-page">
            <h1 className="font-display text-2xl font-semibold text-[#0A1930]">System Logs</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Live operational events</p>
            <div className="bg-[#0A1930] rounded-2xl p-5 font-mono text-xs space-y-1 admin-scroll max-h-[70vh] overflow-y-auto">
                {logs.map((l, i) => (
                    <div key={i} className="text-slate-300 flex gap-3" data-testid={`log-${i}`}>
                        <span className="text-slate-500">{new Date(l.ts).toLocaleString()}</span>
                        <span className={`font-bold ${COLOR[l.level] || "text-slate-300"}`}>[{l.level}]</span>
                        <span className="text-cyan-400">{l.service}:</span>
                        <span>{l.msg}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
